import React, { useState } from 'react';
import { fetchLogisticElementsToItems, add, servicesFilter } from '../capacity.service';
import { useHistory } from 'react-router-dom';
import {
  Form,
  DataType,
  DecimalScale,
  Section,
  SelectOption,
  FormOptions,
  useNotify
} from '@globaleris/ui.library';
import { EnumTipoProductoCapacidad, EnumServicio, EnumTipoNaturaleza } from '../../../api';
import {
  enumValueToKey,
  enumToSelectOptionArray,
  enumToSelectOptionArrayDefaultValues
} from '../../../helpers';
import { useTranslation } from 'react-i18next';
import { getCustomerDomain, notifyError } from '../../../redux';
import Page from '../../../components/page/page';
import { NonAuthRoutes } from '../../../model';
import PageHeader from '../../../components/page/pageHeader';
import { missingFieldsForm } from '../../../helpers/validateFormHelper';

export interface CapacityFormValidations {
  code: boolean
  service: boolean
  logisticElement: boolean
  duration: boolean
  nature: boolean
  initDate: boolean
  endDate: boolean
  signDate: boolean
  capacity: boolean
}

export const initialValidations: CapacityFormValidations = {
  code: false,
  service: false,
  logisticElement: false,
  duration: false,
  nature: false,
  initDate: false,
  endDate: false,
  signDate: false,
  capacity: false 
}

export interface CapacityAddPageProps {}

export const CapacityAddPage: React.FC<CapacityAddPageProps> = () => {
  const { info } = useNotify();

  let history = useHistory();
  const [t] = useTranslation(['capacity', 'enums']);
  const [logisticElements, setLogisticElements] = useState<SelectOption[]>([]);
  const [capacity, setCapacity] = useState<any>({});
  const [duration, setDuration] = useState<any>(false);
  

  //#region Handlers

  const onFormSaveHandler = async (formData: any): Promise<boolean> => {
    if ((formData && missingFieldsForm(sections, formData)) || formData === null) {
      notifyError(t('capacity:forms.add.notifications.error.errorInvalidForm'))
      return false
    } else {    
    if (formData) {
      return await add(capacity).then(response => {
        if (response) {
          info(t('capacity:forms.add.notifications.success.create'));
          setTimeout(
            () => history.push(NonAuthRoutes.capacityEdit, { idCapacity: response.id }),
            2000
          );
          return true;
        } else {
          return false;
        }
      });
    } else {
      history.push(NonAuthRoutes.capacity);
      return false;
    }
  }
  };

  const onFormSaveAndNewHandler = async (formData: any): Promise<boolean> => {
    if (formData) {
      return await add(capacity).then(response => {
        if (response) {
          info(t('capacity:forms.add.notifications.success.create'));
          return true;
        } else {
          return false;
        }
      });
    } else {
      history.push(NonAuthRoutes.capacity);
      return false;
    }
  };

  //#endregion
  
  const [validations, setValidations] = useState<CapacityFormValidations>(initialValidations)

  //#region Form definition

  const sections: Section[] = [
    {
      inputs: [
        {
          name: 'code',
          label: t('capacity:forms.add.inputs.code'),
          type: DataType.TEXT,
          required: true,
        },
        {
          name: 'service',
          label: t('capacity:forms.add.inputs.service'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArrayDefaultValues(
            EnumServicio,
            'enums:EnumServicio',
            servicesFilter
          ),
          onChange: async (data: any, formData) => {
            await getLogisticElements([enumValueToKey(EnumServicio, data)]).then(() => {
              setCapacity(formData);
            });
          },
          required: true,
        },
        {
          name: 'logisticElement.id',
          label: t('capacity:forms.add.inputs.logisticElement'),
          type: DataType.SELECT,
          selectOptions: logisticElements,
          required: true,
          helpText: t('capacity:forms.add.helpTexts.logisticElement')
        },
        {
          name: 'duration',
          label: t('capacity:forms.add.inputs.duration'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(
            EnumTipoProductoCapacidad,
            'enums:EnumTipoProductoCapacidad'
          ),
          required: true,
          onChange: async (durationSelected: any, formData) => {
            setDuration(durationSelected),
            setCapacity((prevState) => ({ ...prevState, hoursQty: undefined }))
          }
        },
        {
          name: 'nature',
          label: t('capacity:forms.add.inputs.nature'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumTipoNaturaleza, 'enums:EnumTipoNaturaleza'),

          required: true,
        },
        {
          name: 'initDate',
          label: t('capacity:forms.add.inputs.initDate'),
          type: DataType.DATE,

          required: true,
        },
        {
          name: 'endDate',
          label: t('capacity:forms.add.inputs.endDate'),
          type: DataType.DATE,

          required: true,
        },
        {
          name: 'signDate',
          label: t('capacity:forms.add.inputs.signDate'),
          type: DataType.DATE,

          required: true,
        },
        {
          name: 'capacity',
          label: t('capacity:forms.add.inputs.capacity'),
          type: DataType.NUMBER,
          decimalScale: DecimalScale.ENERGY,
          required: true,
        },
        {
          name: 'premium',
          label: t('capacity:forms.add.inputs.premium'),
          type: DataType.NUMBER,
          decimalScale: DecimalScale.CURRENCY
        },
        {
          name: 'rawMaterial',
          label: t('capacity:forms.add.inputs.rawMaterial'),
          type: DataType.SWITCH
        },
        {
          name: 'lock',
          label: t('capacity:forms.add.inputs.lock'),
          type: DataType.SWITCH,
          defaultValue: false
        },
        {
          name: 'hoursQty',
          label: t('capacity:forms.add.inputs.hoursQty'),
          maxLength: 200,
          type: DataType.NUMBER,
          display: duration == EnumTipoProductoCapacidad.Intradiario,
          minValue: 1,
          maxValue: 24,
          customDecimalScale: 0,
          errorText: t('capacity:notifications.error.hoursQty')
        }
      ]
    }
  ];

  const options: FormOptions = {
    elevation: 1,
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('capacity:forms.add.analytics')
    },
    onSave: onFormSaveHandler,
    onSaveAndNew: onFormSaveAndNewHandler
  };

  //#endregion

  //#region Functions

  const getLogisticElements = async (serviceTypes: EnumServicio[]) => {
    await fetchLogisticElementsToItems(serviceTypes).then(setLogisticElements);
  };

  //#endregion

  return (
    <Page>
      <PageHeader title={t('capacity:forms.add.title')}></PageHeader>

      <Form sections={sections} data={capacity} options={options} />
    </Page>
  );
};
